<template>
      <div class="mx-auto flex flex-col">
        <div class="flex justify-between items-center">
          <!-- <h2 class="text-xl font-bold">Record Manual Payment</h2> -->
        </div>
        <p>Allocate payments to specific outstanding bills.</p>
        <div>
          <div>
            <StudentSearchInput
              v-model="selectedStudent"
              label="Search Student"
              placeholder="Search by name or student ID"
              :query="GET_STUDENTS_WITH_BILLS"
            />
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div>
              <label for="paymentAmount" class="block text-sm font-medium text-gray-700">Payment Amount</label>
              <InputNumber id="paymentAmount" v-model="paymentAmount" mode="currency" currency="NGN" locale="en-NG" class="w-full mt-1" />
            </div>
            <div>
              <label for="paymentMethod" class="block text-sm font-medium text-gray-700">Payment Method</label>
              <Select id="paymentMethod" v-model="paymentMethod" :options="paymentMethods" placeholder="Select a Method" class="w-full mt-1" />
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div>
              <label for="dateOfPayment" class="block text-sm font-medium text-gray-700">Date of Payment</label>
              <Calendar id="dateOfPayment" v-model="dateOfPayment" class="w-full mt-1" />
            </div>
            <div>
              <label for="referenceNumber" class="block text-sm font-medium text-gray-700">Reference Number (Optional)</label>
              <InputText id="referenceNumber" v-model="referenceNumber" class="w-full mt-1" />
            </div>
          </div>

          <Divider />

          <div>
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-bold">Allocate Payment</h3>
              <Button size="small" label="Suggest Allocation" class="p-button-text" @click="handleSuggestAllocation" :loading="suggesting" />
            </div>

            <DataTable size="small" :value="outstandingBills" v-model:expandedRows="expandedRows" dataKey="id" class="p-datatable-sm">
              <Column :expander="true" style="width: 3rem" />
              <Column header="Bill">
                <template #body="slotProps">
                  <span>Bill due on {{ formatDate(slotProps.data.due_date) }}</span>
                </template>
              </Column>
              <Column header="Balance">
                <template #body="slotProps">
                  {{ formatCurrency(slotProps.data.payment_status_details?.balance ?? slotProps.data.amount) }}
                </template>
              </Column>
              <Column header="Total Allocated">
                <template #body="slotProps">
                  {{ formatCurrency(getBillTotalAllocated(slotProps.data)) }}
                </template>
              </Column>

              <template #expansion="slotProps">
                <div class="p-3">
                  <DataTable size="small" :value="slotProps.data.billDetails" class="p-datatable-sm">
                    <Column field="billItem.name" header="Bill Item"></Column>
                    <Column header="Balance">
                      <template #body="detailSlotProps">
                        {{ formatCurrency(detailSlotProps.data.payment_status_details?.balance ?? detailSlotProps.data.amount) }}
                      </template>
                    </Column>
                    <Column header="Allocation" style="width: 150px">
                      <template #body="detailSlotProps">
                        <InputNumber v-model="detailSlotProps.data.allocated" mode="currency" currency="NGN" locale="en-NG" @update:modelValue="updateAllocation"/>
                      </template>
                    </Column>
                  </DataTable>
                </div>
              </template>
            </DataTable>
          </div>

          <div class="flex justify-end mt-4">
            <div class="text-right">
              <div class="text-gray-500">Total Allocated</div>
              <div class="text-xl font-bold">{{ formatCurrency(totalAllocated) }}</div>
            </div>
            <div class="text-right ml-8">
              <div class="text-gray-500">Remaining to Allocate</div>
              <div class="text-xl font-bold" :class="{'text-primary': remainingToAllocate >= 0, 'text-red-500': remainingToAllocate < 0}">{{ formatCurrency(remainingToAllocate) }}</div>
            </div>
          </div>

          <!-- Allocation validation message -->
          <div v-if="allocationMessage" class="mt-3 p-3 rounded-lg text-sm flex items-center gap-2" :class="allocationMessageClass">
            <i :class="allocationMessageIcon"></i>
            <span>{{ allocationMessage }}</span>
          </div>

          <!-- Enrollment block -->
          <div v-if="enrollmentWarnings.length > 0" class="mt-4 p-3 bg-red-50 border border-red-300 rounded-lg">
            <div class="flex items-start gap-2">
              <i class="pi pi-ban text-red-600 mt-0.5"></i>
              <div>
                <p class="text-sm font-medium text-red-800">Payment Blocked</p>
                <p v-for="(warning, idx) in enrollmentWarnings" :key="idx" class="text-sm text-red-700 mt-1">
                  {{ warning }}
                </p>
              </div>
            </div>
          </div>

          <div class="flex justify-end mt-6">
            <Button size="small" label="Cancel" class="p-button-secondary mr-2" @click="cancel" />
            <Button size="small" label="Record Payment" icon="pi pi-check" @click="recordPayment" :disabled="!isFormValid" v-tooltip.top="formInvalidReason" />
          </div>
        </div>
      </div>
</template>

<script setup lang="ts">

import { useRecordManualPaymentMutation } from '@/types/vue-apollo-helpers';
import { evictBillingQueries } from '@/utils/billingCache';
import { GET_STUDENTS_WITH_BILLS } from '@/graphql/Queries';
import { useToast } from 'primevue/usetoast';
import type { Student, Bill, BillDetail, SuggestedAllocation } from '@/types/composition-functions';
import { useGeneralModalStore } from '@/store/general-modal-store';
import StudentSearchInput from '@/components/common/StudentSearchInput.vue';
import { useSuggestPaymentAllocation } from '@/composables/queries/useSuggestPaymentAllocation';
import { useCheckPaymentEnrollment } from '@/composables/queries/useCheckPaymentEnrollment';
import { formatDateTimeForAPI } from '@/utils/formatDateTimeForAPI';
import { formatCurrency } from '@/utils/currency';

import { extractGraphQLError } from '@/utils/graphqlError';
const toast = useToast();
const router = useRouter();

const expandedRows = ref([]);

// Form fields
const selectedStudent = ref<Student | null>(null);
const paymentAmount = ref<number | null>(null);
const paymentMethod = ref<string | null>(null);
const dateOfPayment = ref<Date | null>(new Date());
const referenceNumber = ref<string | null>(null);
const { toggleModalByName } = useGeneralModalStore();
const paymentMethods = ref(['cash', 'bank_transfer', 'cheque', 'other']);
const suggesting = ref(false);

// Outstanding Bills
type BillWithAllocations = Omit<Bill, 'billDetails'> & {
  billDetails: (BillDetail & { allocated: number | null })[];
};

const getBillTotalAllocated = (bill: BillWithAllocations) =>
  bill.billDetails.reduce((sum, d) => sum + (d.allocated || 0), 0);
const outstandingBills = ref<BillWithAllocations[]>([]);

watch(selectedStudent, (newStudent) => {
  clearEnrollment();
  if (newStudent?.studentBills) {
    outstandingBills.value = newStudent.studentBills.map(bill => ({
      ...bill,
      billDetails: bill.billDetails.map(detail => ({
        ...detail,
        allocated: null,
      })),
    }));
  } else {
    outstandingBills.value = [];
  }
}, { immediate: true });

const { suggest: suggestAllocation } = useSuggestPaymentAllocation();

const {
  warnings: enrollmentWarnings,
  check: checkEnrollment,
  clear: clearEnrollment,
} = useCheckPaymentEnrollment();

const handleSuggestAllocation = async() => {
  if (paymentAmount.value && selectedStudent.value?.id) {
    suggesting.value = true;
    try {
      const suggestions = await suggestAllocation(Number(paymentAmount.value), String(selectedStudent.value.id));
      if (suggestions) {
        outstandingBills.value.forEach(bill => {
          bill.billDetails.forEach(detail => {
            const suggestion = suggestions.find(s => s.bill_detail_id === detail.id);
            detail.allocated = suggestion ? suggestion.suggested_allocation : 0;
          });
        });
        updateAllocation();
      }
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Failed to suggest allocation'), life: 3000 });
    } finally {
      suggesting.value = false;
    }
  }
};

const totalAllocated = ref(0);
const remainingToAllocate = computed(() => (paymentAmount.value || 0) - totalAllocated.value);

const updateAllocation = () => {
  totalAllocated.value = outstandingBills.value.reduce((billSum, bill) => billSum + bill.billDetails.reduce((detailSum, detail) => detailSum + (detail.allocated || 0), 0), 0);

  if (selectedStudent.value?.id) {
    const allocatedDetailIds = outstandingBills.value
      .flatMap((b) => b.billDetails)
      .filter((d) => d.allocated && d.allocated > 0)
      .map((d) => d.id);
    if (allocatedDetailIds.length > 0) {
      checkEnrollment(String(selectedStudent.value.id), allocatedDetailIds);
    } else {
      clearEnrollment();
    }
  }
};

// Actions
const { mutate: recordPaymentMutation, loading: isRecordingPayment } = useRecordManualPaymentMutation({ update: evictBillingQueries });

const isFormValid = computed(() => selectedStudent.value && paymentAmount.value && paymentMethod.value && dateOfPayment.value && remainingToAllocate.value === 0 && enrollmentWarnings.value.length === 0);

const formInvalidReason = computed(() => {
  if (!isFormValid.value) {
    if (!selectedStudent.value) return 'Select a student';
    if (!paymentAmount.value) return 'Enter a payment amount';
    if (!paymentMethod.value) return 'Select a payment method';
    if (!dateOfPayment.value) return 'Select a payment date';
    if (enrollmentWarnings.value.length > 0) return 'Student is not enrolled in the bill\'s academic calendar';
    if (remainingToAllocate.value > 0) return `${formatCurrency(remainingToAllocate.value)} still unallocated`;
    if (remainingToAllocate.value < 0) return `Over-allocated by ${formatCurrency(Math.abs(remainingToAllocate.value))}`;
  }
  return '';
});

const allocationMessage = computed(() => {
  if (!paymentAmount.value || paymentAmount.value <= 0) return '';
  if (totalAllocated.value === 0 && outstandingBills.value.length > 0) return 'Allocate the payment amount to outstanding bills below.';
  if (remainingToAllocate.value > 0) return `${formatCurrency(remainingToAllocate.value)} remaining to allocate.`;
  if (remainingToAllocate.value < 0) return `Over-allocated by ${formatCurrency(Math.abs(remainingToAllocate.value))}. Reduce allocations to match the payment amount.`;
  if (remainingToAllocate.value === 0 && totalAllocated.value > 0) return 'Allocation matches payment amount.';
  return '';
});

const allocationMessageClass = computed(() => {
  if (remainingToAllocate.value === 0 && totalAllocated.value > 0) return 'bg-primary/10 text-primary border border-primary/20';
  if (remainingToAllocate.value < 0) return 'bg-red-50 text-red-700 border border-red-200';
  return 'bg-yellow-50 text-yellow-700 border border-yellow-200';
});

const allocationMessageIcon = computed(() => {
  if (remainingToAllocate.value === 0 && totalAllocated.value > 0) return 'pi pi-check-circle';
  if (remainingToAllocate.value < 0) return 'pi pi-exclamation-triangle';
  return 'pi pi-info-circle';
});

const recordPayment = async() => {
  if (!isFormValid.value) return;

  const paymentData = {
    student_id: selectedStudent.value!.id,
    amount: paymentAmount.value,
    payment_method: paymentMethod.value,
    payment_date: formatDateTimeForAPI(dateOfPayment.value),
    transaction_reference: referenceNumber.value,
    allocations: outstandingBills.value
      .flatMap(bill => bill.billDetails)
      .filter(detail => detail.allocated && detail.allocated > 0)
      .map(detail => ({ bill_detail_id: detail.id, amount: detail.allocated })),
  };

  try {
    const { data } = (await recordPaymentMutation({ input: paymentData }))!;
    if (data.recordManualPayment) {
      toast.add({ severity: 'success', summary: 'Success', detail: 'Payment recorded successfully', life: 3000 });
      router.push('/admin/billing');
    }
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Failed to record payment'), life: 3000 });
  }
};

const cancel = () => {
  toggleModalByName('showRecordManualPaymentDialog');
};

// Formatting
const formatDate = (value: string | undefined | null) => {
  if (!value) return '';
  return new Date(value).toLocaleDateString();
};

</script>

<style scoped>
/* Add any additional styles here */
</style>
